import UrlTypeIcon from '@components/UrlTypeIcon/UrlTypeIcon';
import {
  faFileCertificate,
  faHeading,
  faLock,
  faUser,
  faUserAstronaut,
} from '@fortawesome/pro-regular-svg-icons';
import { faBuilding, faEnvelope, faMapMarkerAlt, faPhone, faUsers } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CardUrl, FieldTypes } from 'types';

import styles from './FieldButton.module.scss';

type FieldButtonProps = {
  onClick: (type: FieldTypes, subType: CardUrl['type'] | undefined) => void;
  canLock: boolean;
  locked: boolean;
  onLock: (type: string) => void;
  label: string;
  icon: React.ReactElement;
  disabled?: boolean;
} & (
  | {
      type: Exclude<FieldTypes, 'url'>;
      subType?: undefined;
    }
  | {
      type: 'url';
      subType: CardUrl['type'];
    }
);

const FieldButton: React.FC<React.PropsWithChildren<FieldButtonProps>> = (props) => {
  const { label, icon, type, subType, onClick, locked, canLock, onLock, disabled = false } = props;

  function handleKeyPress(event: React.KeyboardEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest(`.${styles['lock-btn'].split(' ').join('.')}`)) {
      event.stopPropagation();
      return;
    }

    if (event.key === ' ') {
      event.preventDefault();
      onClick(type, subType);
    }
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if ((event.target as HTMLElement).closest(`.${styles['lock-btn'].split(' ').join('.')}`)) {
      event.stopPropagation();
      return;
    }

    if (event.key === 'Enter') {
      onClick(type, subType);
    } else if (event.key === ' ') {
      event.preventDefault();
    }
  }

  return (
    <>
      {(canLock || !locked) && (
        <div
          id={`${type}-field-btn`}
          className={styles['field-button']}
          onClick={(event) => {
            const wasLockBtnClicked = (event.target as HTMLElement).closest(
              `.${styles['lock-btn'].split(' ').join('.')}`,
            );
            if (!wasLockBtnClicked && !disabled) {
              onClick(type, subType);
            }
          }}
          tabIndex={disabled ? -1 : 0}
          role={disabled ? undefined : 'button'}
          onKeyUp={handleKeyPress}
          onKeyDown={handleKeyDown}
          data-disabled={disabled}
        >
          <div className={styles['field-button-icon-container']}>{icon}</div>
          <div className={styles['field-button-label']}>{label}</div>
          {canLock && !disabled && (
            <button
              onClick={() => onLock(subType || type)}
              className={styles['lock-btn']}
              data-is-locked={locked}
              id={`${type}-lock-btn`}
            >
              <FontAwesomeIcon icon={faLock} />
            </button>
          )}
        </div>
      )}
      {!canLock && locked && (
        <div className={styles['field']}>
          <div className={styles['field-button-icon-container']}>{icon}</div>
          <div className={styles['field-button-label']}>{label}</div>
          <div className={styles['lock']}>
            <FontAwesomeIcon icon={faLock} />
          </div>
        </div>
      )}
    </>
  );
};

type FieldButtonIconProps =
  | {
      type: Exclude<FieldTypes, 'url'>;
      subType?: undefined;
    }
  | {
      type: 'url';
      subType: CardUrl['type'];
    };

const FieldButtonIcon: React.FC<React.PropsWithChildren<FieldButtonIconProps>> = (props) => {
  // You might be wondering why I don't use the destructured value here. I'ts due to the fact that when you
  // destructure a variable it infers the type, and typescript does not propogate these back to the source
  // https://github.com/Microsoft/TypeScript/issues/27927#issuecomment-442921711
  switch (props.type) {
    case 'name':
      return <FontAwesomeIcon icon={faUser} />;
    case 'accreditations':
      // Could also be <i class="far fa-user-graduate"></i>
      return <FontAwesomeIcon icon={faFileCertificate} />;
    case 'org_name':
      return <FontAwesomeIcon icon={faBuilding} />;
    case 'department':
      return <FontAwesomeIcon icon={faUsers} />;
    case 'job_title':
      return <FontAwesomeIcon icon={faUserAstronaut} />;
    case 'headline':
      return <FontAwesomeIcon icon={faHeading} />;
    case 'url':
      return <UrlTypeIcon type={props.subType} />;
    case 'phone':
      return <FontAwesomeIcon icon={faPhone} />;
    case 'address':
      return <FontAwesomeIcon icon={faMapMarkerAlt} />;
    case 'email':
      return <FontAwesomeIcon icon={faEnvelope} />;
  }
};

export default FieldButton;
export { FieldButtonIcon };
